
// EditLeadDrawer.jsx
import React, { useState, useEffect } from 'react';
import {
    Drawer, Form, Input, Button, Select, AutoComplete,
    Space, Tag, message, Row, Col, Typography,
    Card, Alert, DatePicker,
    InputNumber, Divider, Tabs, Modal,
    Descriptions, Badge, Table,
    Checkbox
} from 'antd';
import {
    EditOutlined, SaveOutlined, CloseOutlined,
    CommentOutlined, FieldTimeOutlined,
    ExclamationCircleOutlined,
    DollarOutlined, CreditCardOutlined,
    HistoryOutlined, EyeOutlined,
    DownloadOutlined, SendOutlined,
    PhoneOutlined, MailOutlined,
    HomeOutlined, TransactionOutlined,
    FileTextOutlined, CheckCircleOutlined,
    CalendarOutlined
} from '@ant-design/icons';
import API from '../../../API/API';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import {
    TRACKING_PROMISE_DAYS_ERROR,
    TRACKING_PROMISE_DAYS_PLACEHOLDER,
    getTrackingPromiseDaysErrorMessage,
    isValidTrackingPromiseDays,
    normalizeTrackingPromiseDaysInput
} from '../utils/trackingPromiseDays';
import { toCents, roundMoney, getBalance } from '../../../utils/money';

dayjs.extend(utc);
dayjs.extend(timezone);

const { Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;
const { TabPane } = Tabs;
const TYPE_OF_LEAD_OPTIONS = ['Inbound Call', 'Live', 'Follow-Up'];
const getTypeOfLeadFormValue = (value) => TYPE_OF_LEAD_OPTIONS.includes(value) ? value : undefined;

// True when the lead already has a billing address and the shipping address matches it.
const isShippingSameAsBilling = (lead) => {
    const b = lead?.billingAddress || {};
    const s = lead?.shippingAddress || {};
    const norm = (v) => (v || '').toString().trim().toLowerCase();
    if (!norm(b.street)) return false;
    return ['street', 'city', 'state', 'zip'].every((k) => norm(b[k]) === norm(s[k]));
};

const EditLeadDrawer = ({
    visible,
    onClose,
    onUpdate,
    lead,
    currentStage,onTabChange
}) => {
    const [form] = Form.useForm();
    const sameAsBillingValue = Form.useWatch('sameAsBilling', form);
    const [loading, setLoading] = useState(false);
    const [selectedStage, setSelectedStage] = useState(currentStage);
    const [hasMadeChanges, setHasMadeChanges] = useState(false);
    const [availableStages, setAvailableStages] = useState([]);
    const [followUpDate, setFollowUpDate] = useState(null);
    const [showQuoteValue, setShowQuoteValue] = useState(false);
    const [quoteAmount, setQuoteAmount] = useState(0);
    const [showCommentsField, setShowCommentsField] = useState(false);
    const [paymentMethod, setPaymentMethod] = useState('Card');
    const [paymentHistory, setPaymentHistory] = useState([]);
    // Masked hint for a previously saved card (never the real number) — { cardType, lastFourDigits }
    const [savedCardOnFile, setSavedCardOnFile] = useState(null);
const [salesMadeDate, setSalesMadeDate] = useState(null);
const [showSalesMadeDatePicker, setShowSalesMadeDatePicker] = useState(false);
    // Stages that require MANDATORY comments
    const stagesRequiringMandatoryComments = [
        "Call Back",
        "Part Not Available",
        "Wrong Number",
        "Not Interested",
        "Hang Up",
        "Fake",
        "Error",
        "Negotiation",
    ];
    // Stages that show the Comments box but do NOT require it
    const stagesWithOptionalComments = [
        "Voice Mail",
    ];
    const [fullPaymentAlert, setFullPaymentAlert] = useState(false);

const warrantyOptions = [
    { value: '30 Days', label: '30 Days' },
    { value: '60 Days', label: '60 Days' },
    { value: '90 Days', label: '90 Days' },
    { value: '120 Days', label: '120 Days' },
    { value: '150 Days', label: '150 Days' },
    { value: '180 Days', label: '180 Days' },
    { value: '1 Year', label: '1 Year' },
    { value: '2 Years', label: '2 Years' },
    { value: '3 Years', label: '3 Years' },
    { value: '4 Years', label: '4 Years' },   
    { value: '5 Years', label: '5 Years' },
    { value: 'No Warranty', label: 'No Warranty' }
];
    // Payment methods
    const paymentMethods = [
      'Card',
                'Payment Link',
                'Cashapp',
                'Venmo',
                'PayPal',
                'Pay Tomorrow',
                'ACH',
                'Zelle',
                'Cash',
                'Check Payment',
                'Wire Transfer',
    ];

    // Card types
    const cardTypes = [
        'Visa',
        'MasterCard',
        'American Express',
        'Discover',
        'JCB',
        'Diners Club',
        'UnionPay',
        'Other'
    ];

    // Stage transition rules
    const stageTransitionRules = {
        "New": [
            "Call Back",
            "Error",
            "Fake",
            "Follow-Up",
            "Hang Up",
            "Not Interested",
            "Part Not Available",
            "Quoted",
            "Pending Payment",
            "Payment Details",
            "Sales Made",
            "Test Lead",
            "Voice Mail",
            "Wrong Number"
        ],
        "Call Back": [
            "Follow-Up",
            "Hang Up",
            "Not Interested",
            "Part Not Available",
            "Quoted",
            "Pending Payment",
            "Payment Details",
            "Sales Made",
            "Voice Mail",
            "Wrong Number"
        ],
        "Voice Mail": [
            "Call Back",
            "Follow-Up",
            "Hang Up",
            "Not Interested",
            "Part Not Available",
            "Quoted",
            "Pending Payment",
            "Payment Details",
            "Sales Made",
            "Wrong Number"
        ],
        "Wrong Number": [
            "Call Back",
            "Error",
            "Fake",
            "Follow-Up",
            "Hang Up",
            "Not Interested",
            "Part Not Available",
            "Quoted",
            "Pending Payment",
            "Payment Details",
            "Sales Made",
            "Test Lead",
            "Voice Mail",

        ],
        "Follow-Up": [
            "Hang Up",
            "Negotiation",
            "Not Interested",
            "Part Not Available",
            "Quoted",
            "Pending Payment",
            "Payment Details",
            "Sales Made",
            "Wrong Number"
        ],
        "Quoted": [
            "Negotiation",
            "Not Interested",
            "Part Not Available",
            "Follow-Up",
            "Pending Payment",
            "Payment Details",
            "Sales Made"
        ],
        "Negotiation": [
            "Not Interested",
            "Part Not Available",
            "Pending Payment",
            "Payment Details",
            "Sales Made"
        ],
        "Payment Details": [
            "Not Interested",
            "Part Not Available",
            "Payment Completed",
            "Quoted",
            "Sales Made"
        ],
        "Pending Payment": [
            "Pending Payment",
            "Payment Details",
            "Sales Made"
        ],
        "Payment Completed": [
            "Sales Made"
        ],
        "Sales Made": [],
        "Part Not Available": [
            "Call Back",
            "Follow-Up",
            "New"
        ],

        "Not Interested": [],
        "Hang Up": [],
        "Fake": [],
        "Test Lead": [],
        "Error": [
            "Call Back",
            "Error",
            "Fake",
            "Follow-Up",
            "Hang Up",
            "New",
            "Not Interested",
            "Part Not Available",
            "Quoted",
            "Pending Payment",
            "Payment Details",
            "Test Lead",
            "Voice Mail",
            "Wrong Number"
        ]
    };
const validateRequiredFieldsForSalesMade = () => {
    const missingFields = [];
    
    // Check vehicleInfo
    if (!lead?.vehicleInfo) {
        missingFields.push('Vehicle Information section is completely missing');
    } else {
        if (!lead.vehicleInfo.make || lead.vehicleInfo.make.toString().trim() === '') {
            missingFields.push('Vehicle Make');
        }
        if (!lead.vehicleInfo.model || lead.vehicleInfo.model.toString().trim() === '') {
            missingFields.push('Vehicle Model');
        }
        if (!lead.vehicleInfo.year || lead.vehicleInfo.year.toString().trim() === '') {
            missingFields.push('Vehicle Year');
        }
        if (!lead.vehicleInfo.part || lead.vehicleInfo.part.toString().trim() === '') {
            missingFields.push('Vehicle Part');
        }
        if (!lead.vehicleInfo.vin || lead.vehicleInfo.vin.toString().trim() === '') {
            missingFields.push('Vehicle VIN');
        }
    }
    
    // Check personalInfo
    if (!lead?.personalInfo) {
        missingFields.push('Personal Information section is completely missing');
    } else {
        if (!lead.personalInfo.fullName || lead.personalInfo.fullName.toString().trim() === '') {
            missingFields.push('Customer Full Name');
        }
        if (!lead.personalInfo.email || lead.personalInfo.email.toString().trim() === '') {
            missingFields.push('Customer Email');
        }
        if (!lead.personalInfo.phoneNumber || lead.personalInfo.phoneNumber.toString().trim() === '') {
            missingFields.push('Customer Phone Number');
        }
    }
    
    // Check Warranty
    if (!lead?.warranty || lead.warranty.toString().trim() === '') {
        missingFields.push('Warranty');
    }
    
    // ✅ ADD MILES PROMISE CUSTOMER CHECK
    // Note: This checks if the field exists in the lead data
    // Since it's being added now, it might not exist in existing leads
    // So we don't add it to missing fields if it's a new requirement
    
    return missingFields;
};;
// A Payment Completed or Sales Made lead whose total was raised after payment
// owes money again. Offer "Payment Details" so the balance can be recorded;
// those stages otherwise have no way to add a payment.
const BALANCE_TOP_UP_STAGES = ["Payment Completed", "Sales Made"];
const withBalanceTopUp = (stage, stages) => {
    if (BALANCE_TOP_UP_STAGES.includes(stage)
        && !stages.includes("Payment Details")
        && calculatePaymentTotals().balance > 0) {
        return ["Payment Details", ...stages];
    }
    return stages;
};
useEffect(() => {
    if (lead && visible) {
        resetForm();

        // Set available stages based on current stage and rules
        if (currentStage && stageTransitionRules[currentStage]) {
            setAvailableStages(withBalanceTopUp(currentStage, stageTransitionRules[currentStage]));
        } else {
            setAvailableStages([]);
        }

        // Get the last quote from lead if exists
        if (lead.quotedValue && lead.quotedValue.length > 0) {
            const lastQuote = lead.quotedValue[lead.quotedValue.length - 1];
            setQuoteAmount(lastQuote.price || 0);
        } else {
            setQuoteAmount(0);
        }

        // Load payment history if exists
        if (lead.advancePayments) {
            setPaymentHistory(lead.advancePayments);
        }
        
        // ✅ Set form values for address fields
        form.setFieldsValue({
            billingStreet: lead?.billingAddress?.street || '',
            billingCity: lead?.billingAddress?.city || '',
            billingState: lead?.billingAddress?.state || '',
            billingZip: lead?.billingAddress?.zip || '',
            shippingStreet: lead?.shippingAddress?.street || '',
            shippingCity: lead?.shippingAddress?.city || '',
            shippingState: lead?.shippingAddress?.state || '',
            shippingZip: lead?.shippingAddress?.zip || '',
            businessName: lead?.bussinessName || '',
            sameAsBilling: isShippingSameAsBilling(lead),
            attnName: lead?.attnName || lead?.personalInfo?.fullName || '',
            warranty: lead?.warranty,
            orderNumber: lead?.orderNumber || '',
            trackingPromiseDays: lead?.trackingPromiseDays,
            typeOfLead: getTypeOfLeadFormValue(lead?.typeOfLead),
        });

        // ✅ Reload previously saved SAFE card metadata (never the real card number or CVV).
        // Find the most recent Card-method payment, if any, to prefill cardType/expirationDate/
        // cardHolderName so the user doesn't have to re-enter them for a new payment.
        // The backend only ever appends new payments (advancePayments.push) and updates existing
        // ones in place by id/index — it never reorders the array — so array order is a reliable
        // chronological signal and is preferable to sorting by `date`, which can be missing/
        // malformed on older records. cardType/lastFourDigits/expirationDate/cardHolderName below
        // are always read off this SAME selected record, never mixed across records.
        const cardPayments = (lead?.advancePayments || []).filter(p => p?.paymentMethod === 'Card');
        const latestCardPayment = cardPayments.length
            ? cardPayments[cardPayments.length - 1]
            : null;

        form.setFieldsValue({
            // Card number and CVV must always start blank — a new card requires re-entry
            cardNumber: undefined,
            cvv: undefined,
            cardType: latestCardPayment?.cardType || undefined,
            expirationDate: latestCardPayment?.expirationDate || undefined,
            cardHolderName: latestCardPayment?.cardHolderName || undefined,
        });

        setSavedCardOnFile(
            latestCardPayment?.lastFourDigits
                ? { cardType: latestCardPayment.cardType, lastFourDigits: latestCardPayment.lastFourDigits }
                : null
        );
    }
}, [lead, visible, currentStage]);
const resetForm = () => {
    form.setFieldsValue({
        leadStage: undefined,
        comments: '',
        followUpDate: null,
        warranty: lead?.warranty,
        totalAmount: undefined,
        paymentAmount: undefined,
        paymentMethod: 'Card',
        salesMadeDate: null,
        orderNumber: lead?.orderNumber || '',
        milesPromiseCustomer: '',
        trackingPromiseDays: lead?.trackingPromiseDays,
        typeOfLead: getTypeOfLeadFormValue(lead?.typeOfLead),
        // ✅ Add billing address initial values
        billingStreet: lead?.billingAddress?.street || '',
        billingCity: lead?.billingAddress?.city || '',
        billingState: lead?.billingAddress?.state || '',
        billingZip: lead?.billingAddress?.zip || '',
        // ✅ Add shipping address initial values
        shippingStreet: lead?.shippingAddress?.street || '',
        shippingCity: lead?.shippingAddress?.city || '',
        shippingState: lead?.shippingAddress?.state || '',
        shippingZip: lead?.shippingAddress?.zip || '',
        // ✅ Business name
        businessName: lead?.bussinessName || '',
        sameAsBilling: isShippingSameAsBilling(lead),
        attnName: lead?.attnName || lead?.personalInfo?.fullName || '',
    });
    
    // Reset states
    setSelectedStage(null);
    setFollowUpDate(null);
    setHasMadeChanges(false);
    setShowQuoteValue(false);
    setShowCommentsField(false);
    setShowSalesMadeDatePicker(false);
    setSalesMadeDate(null);
    setQuoteAmount(0);
    setPaymentMethod('Card');
};

const handleStageChange = (value) => {
    setSelectedStage(value);
    setHasMadeChanges(true);
    
    // Show Sales Made date picker when Sales Made stage is selected
if (value === "Sales Made") {
    setShowSalesMadeDatePicker(true);
    // DO NOT set default date - leave it empty
    setSalesMadeDate(null);
    form.setFieldsValue({ salesMadeDate: null });
} else {
        setShowSalesMadeDatePicker(false);
        setSalesMadeDate(null);
    }
        if (value === "Payment Details") {
        // Calculate current balance
        const payments = lead?.advancePayments || [];
        const uniqueMap = new Map();
        payments.forEach(payment => {
            const key = payment.transactionId || payment.id;
            if (!uniqueMap.has(key) || 
                new Date(payment.date) > new Date(uniqueMap.get(key).date)) {
                uniqueMap.set(key, payment);
            }
        });
        const uniquePayments = Array.from(uniqueMap.values());
        const totalPaid = roundMoney(uniquePayments.reduce((sum, p) =>
            p.status === 'Completed' ? sum + (Number(p.amount) || 0) : sum, 0));
        const balance = getBalance(lead?.totalAmount, totalPaid);

        // Auto-populate payment amount with balance
        if (balance > 0) {
            form.setFieldsValue({ paymentAmount: balance });
        } else {
            form.setFieldsValue({ paymentAmount: undefined });
        }
    } else if (value === "Pending Payment") {
        // Clear payment amount for Pending Payment
        form.setFieldsValue({ paymentAmount: undefined });
    }
    // Validate required fields for Sales Made
    if (value === "Sales Made") {
        const { totalAmount, totalPaid, balance, isReady } = getSalesMadePaymentSummary();

        if (!isReady) {
            Modal.error({
                title: 'Cannot Move to Sales Made',
                content: (
                    <div>
                        {totalAmount <= 0 ? (
                            <>
                                <p>Total amount is not set.</p>
                                <p>Please set a total amount first (add a quote).</p>
                            </>
                        ) : (
                            <>
                                <p>Full payment is required before moving to Sales Made.</p>
                                <p>
                                    Total: <strong>{formatPaymentAmount(totalAmount)}</strong> | Paid: <strong>{formatPaymentAmount(totalPaid)}</strong> | Balance: <strong>{formatPaymentAmount(balance)}</strong>
                                </p>
                            </>
                        )}
                        <Button
                            type="primary"
                            size="small"
                            onClick={() => {
                                if (onTabChange) {
                                    onTabChange(totalAmount <= 0 ? 'quoted' : 'payment-detail');
                                }
                                onClose();
                            }}
                            style={{ marginTop: '12px' }}
                        >
                            {totalAmount <= 0 ? 'Go to Quoted Tab' : 'Go to Payment Details Tab'}
                        </Button>
                    </div>
                ),
                width: 450
            });
            setSelectedStage(null);
            return;
        }

        const missingFields = validateRequiredFieldsForSalesMade();
        
        if (missingFields.length > 0) {
            // Create a button to go to Details tab
            const goToDetailsButton = (
                <Button 
                    type="primary" 
                    size="small"
                    onClick={() => {
                        if (onTabChange) {
                            onTabChange('details');
                        }
                        // Close the drawer
                        onClose();
                    }}
                    style={{ marginTop: '12px' }}
                >
                    Go to Details Tab
                </Button>
            );
            
            // Create formatted missing fields list
            const missingFieldsList = missingFields.map((field, index) => 
                `${index + 1}. ${field}`
            ).join('\n');
            
            // Show modal with button instead of just message.error
            Modal.error({
                title: 'Cannot Move to Sales Made',
                content: (
                    <div>
                        <p><strong>Missing required fields:</strong></p>
                        <pre style={{ 
                            backgroundColor: '#f5f5f5', 
                            padding: '8px', 
                            borderRadius: '4px',
                            marginTop: '8px',
                            marginBottom: '16px',
                            fontFamily: 'monospace'
                        }}>
                            {missingFieldsList}
                        </pre>
                        <p>Please fill in all required fields before moving to Sales Made stage.</p>
                        {goToDetailsButton}
                    </div>
                ),
                width: 500,
                okText: 'Close',
                onOk: () => {
                    // Optional: Any action when modal is closed
                }
            });
            
            setSelectedStage(null);
            return;
        }
    }

    // Show quote value ONLY for Quoted stage
    if (value === "Quoted") {
        setShowQuoteValue(true);
    } else {
        setShowQuoteValue(false);
    }

    // Show comments field for Negotiation and other stages that require comments
    if (value === "Negotiation" || stagesRequiringMandatoryComments.includes(value) || stagesWithOptionalComments.includes(value)) {
        setShowCommentsField(true);
    } else {
        setShowCommentsField(false);
    }

    // Reset follow-up date when stage changes
    if (value !== "Follow-Up") {
        setFollowUpDate(null);
        form.setFieldsValue({ followUpDate: null });
    }
};
    const handleFollowUpDateChange = (date) => {
        setFollowUpDate(date);
        setHasMadeChanges(true);
    };

    const handleCancel = () => {
        resetForm();
        onClose();
    };

    const numberToWords = (num) => {
        if (!num || isNaN(num)) return "";

        const ones = [
            "", "One", "Two", "Three", "Four", "Five", "Six",
            "Seven", "Eight", "Nine", "Ten", "Eleven", "Twelve",
            "Thirteen", "Fourteen", "Fifteen", "Sixteen",
            "Seventeen", "Eighteen", "Nineteen",
        ];
        const tens = [
            "", "", "Twenty", "Thirty", "Forty",
            "Fifty", "Sixty", "Seventy", "Eighty", "Ninety",
        ];

        const toWords = (n) => {
            if (n < 20) return ones[n];
            if (n < 100)
                return tens[Math.floor(n / 10)] + (n % 10 ? " " + ones[n % 10] : "");
            if (n < 1000)
                return (
                    ones[Math.floor(n / 100)] +
                    " Hundred" +
                    (n % 100 ? " " + toWords(n % 100) : "")
                );
            if (n < 1_000_000)
                return (
                    toWords(Math.floor(n / 1000)) +
                    " Thousand" +
                    (n % 1000 ? " " + toWords(n % 1000) : "")
                );
            if (n < 1_000_000_000)
                return (
                    toWords(Math.floor(n / 1_000_000)) +
                    " Million" +
                    (n % 1_000_000 ? " " + toWords(n % 1_000_000) : "")
                );
            return "Amount too large";
        };

        const dollars = Math.floor(num);
        const cents = Math.round((num - dollars) * 100);

        let result = `${toWords(dollars)} Dollar${dollars !== 1 ? "s" : ""}`;
        if (cents > 0) {
            result += ` and ${toWords(cents)} Cent${cents !== 1 ? "s" : ""}`;
        }

        return result;
    };

    // Helper functions for payment validation
    const validateCreditCard = (number) => {
        // Luhn algorithm
        let sum = 0;
        let shouldDouble = false;
        const cleaned = number.replace(/\s/g, '');
        for (let i = cleaned.length - 1; i >= 0; i--) {
            let digit = parseInt(cleaned.charAt(i));
            if (shouldDouble) {
                digit *= 2;
                if (digit > 9) digit -= 9;
            }
            sum += digit;
            shouldDouble = !shouldDouble;
        }
        return sum % 10 === 0;
    };

    const detectCardType = (cardNumber) => {
        const cleaned = cardNumber.replace(/\s/g, '');

        if (/^4/.test(cleaned)) return 'Visa';
        if (/^5[1-5]/.test(cleaned)) return 'MasterCard';
        if (/^3[47]/.test(cleaned)) return 'American Express';
        if (/^6(?:011|4[4-9]|5)/.test(cleaned)) return 'Discover';
        if (/^(?:2131|1800|35)/.test(cleaned)) return 'JCB';
        if (/^3[68]/.test(cleaned)) return 'Diners Club';
        if (/^62/.test(cleaned)) return 'UnionPay';
        return 'Other';
    };

    const getPaymentMethodIcon = (method) => {
        switch (method?.toLowerCase()) {
            case 'card': return <CreditCardOutlined />;
            case 'payment link': return <SendOutlined />;
            case 'check': case 'check payment': return <FileTextOutlined />;
            case 'zelle': return <TransactionOutlined />;
            case 'cashapp': case 'venmo': case 'paypal': return <DollarOutlined />;
            case 'wire transfer': return <TransactionOutlined />;
            default: return <DollarOutlined />;
        }
    };

    const getPaymentMethodColor = (method) => {
        switch (method?.toLowerCase()) {
            case 'card': return 'blue';
            case 'payment link': return 'purple';
            case 'check': case 'check payment': return 'orange';
            case 'zelle': return 'green';
            case 'cashapp': return 'green';
            case 'venmo': return '#3d95ce';
            case 'paypal': return '#003087';
            case 'wire transfer': return 'cyan';
            default: return 'default';
        }
    };

    const renderPaymentMethodFields = () => {
        switch (paymentMethod) {
            case 'Card':
                return (
                    <>
                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item
                                    label="Card Number"
                                    name="cardNumber"
                                    extra={savedCardOnFile ? (
                                        <span style={{ color: '#8c8c8c' }}>
                                            Card on file: •••• {savedCardOnFile.lastFourDigits}
                                            {savedCardOnFile.cardType ? ` (${savedCardOnFile.cardType})` : ''} — enter a new card number to charge a different card
                                        </span>
                                    ) : undefined}
                                    rules={[
                                        { required: paymentMethod === 'Card', message: 'Please enter card number' },
                                        {
                                            validator: (_, value) => {
                                                if (paymentMethod !== 'Card' || !value) {
                                                    return Promise.resolve();
                                                }
                                                const cleaned = value?.replace(/\s/g, '');
                                                if (!cleaned || !/^\d{13,19}$/.test(cleaned)) {
                                                    return Promise.reject('Card number must be 13-19 digits');
                                                }
                                                if (!validateCreditCard(cleaned)) {
                                                    return Promise.reject('Invalid card number');
                                                }
                                                const detectedType = detectCardType(cleaned);
                                                form.setFieldsValue({ cardType: detectedType });
                                                return Promise.resolve();
                                            }
                                        }
                                    ]}
                                >
                                    <Input
                                        placeholder="1234 5678 9012 3456"
                                        maxLength={23}
                                        onChange={(e) => {
                                            let value = e.target.value.replace(/\D/g, '');
                                            if (value.length > 19) value = value.substring(0, 19);
                                            const formatted = value.replace(/(\d{4})(?=\d)/g, '$1 ');
                                            form.setFieldsValue({ cardNumber: formatted });
                                            if (value.length >= 4) {
                                                const detectedType = detectCardType(value);
                                                form.setFieldsValue({ cardType: detectedType });
                                            }
                                        }}
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    label="Card Type"
                                    name="cardType"
                                    rules={[{ required: paymentMethod === 'Card', message: 'Please select card type' }]}
                                >
                                    <Select placeholder="Card type will auto-detect">
                                        {cardTypes.map(type => (
                                            <Option key={type} value={type}>{type}</Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                        </Row>

                        <Row gutter={16}>
                            <Col span={8}>
                                <Form.Item
                                    label="Expiration Date"
                                    name="expirationDate"
                                    rules={[
                                        { required: paymentMethod === 'Card', message: 'MM/YY required' },
                                        {
                                            pattern: /^(0[1-9]|1[0-2])\/([0-9]{2})$/,
                                            message: 'Format: MM/YY (e.g., 12/25)'
                                        }
                                    ]}
                                >
                                    <Input
                                        placeholder="MM/YY"
                                        maxLength={5}
                                        onChange={(e) => {
                                            let value = e.target.value.replace(/\D/g, '');
                                            if (value.length > 2) {
                                                value = value.substring(0, 2) + '/' + value.substring(2, 4);
                                            }
                                            form.setFieldsValue({ expirationDate: value });
                                        }}
                                    />
                                </Form.Item>
                            </Col>
                        <Col span={8}>
    <Form.Item
        label="CVV"
        name="cvv"  // This is the form field name
        rules={[
            { required: paymentMethod === 'Card', message: 'CVV required' },
            { pattern: /^[0-9]{3,4}$/, message: '3–4 digits only' }
        ]}
    >
        <Input.Password maxLength={4} placeholder="CVV" />
    </Form.Item>
</Col>
                            <Col span={8}>
                                <Form.Item
                                    label="Card Holder Name"
                                    name="cardHolderName"
                                    rules={[{ required: paymentMethod === 'Card', message: 'Please enter card holder name' }]}
                                >
                                    <Input placeholder="John Doe" />
                                </Form.Item>
                            </Col>
                        </Row>
                    </>
                );

            // case 'Other':
            //     return (
            //         <Form.Item
            //             label="Payment Details"
            //             name="description"
            //             rules={[{ required: paymentMethod === 'Other', message: 'Please enter payment details' }]}
            //         >
            //             <TextArea
            //                 rows={3}
            //                 placeholder="Enter payment method and details..."
            //             />
            //         </Form.Item>
            //     );

            default:
                return null;
        }
    };

    const handleViewPaymentDetails = () => {
        Modal.info({
            title: 'Payment History',
            width: 1000,
            content: (
                <div>
                    {paymentHistory.length > 0 ? (
                        <Table
                            dataSource={paymentHistory}
                            columns={[
                                {
                                    title: 'Date',
                                    dataIndex: 'date',
                                    key: 'date',
                                    render: (date) => dayjs(date).format('MM-DD-YYYY hh:mm A'),
                                    sorter: (a, b) => new Date(a.date) - new Date(b.date),
                                    defaultSortOrder: 'descend'
                                },
                                {
                                    title: 'Amount',
                                    dataIndex: 'amount',
                                    key: 'amount',
                                    render: (amount) => (
                                        <Text strong style={{ color: '#52c41a' }}>
                                            ${amount?.toFixed(2)}
                                        </Text>
                                    ),
                                    align: 'right'
                                },
                                {
                                    title: 'Method',
                                    dataIndex: 'paymentMethod',
                                    key: 'paymentMethod',
                                    render: (method) => (
                                        <Tag
                                            color={getPaymentMethodColor(method)}
                                            icon={getPaymentMethodIcon(method)}
                                        >
                                            {method}
                                        </Tag>
                                    )
                                },
                                {
                                    title: 'Status',
                                    dataIndex: 'status',
                                    key: 'status',
                                    render: (status) => (
                                        <Badge
                                            status={status === 'Completed' ? 'success' : 'processing'}
                                            text={status}
                                        />
                                    )
                                },
                                {
                                    title: 'Description',
                                    dataIndex: 'description',
                                    key: 'description',
                                    ellipsis: true
                                },
                                {
                                    title: 'Actions',
                                    key: 'actions',
                                    render: (_, record, index) => (
                                        <Button
                                            type="link"
                                            size="small"
                                            icon={<EyeOutlined />}
                                            onClick={() => {
                                                Modal.info({
                                                    title: `Payment Details - #${index + 1}`,
                                                    width: 800,
                                                    content: (
                                                        <div style={{ marginTop: 20 }}>
                                                            <Descriptions column={2} bordered size="small">
                                                                <Descriptions.Item label="Date">
                                                                    {dayjs(record.date).format('MM-DD-YYYY hh:mm A')}
                                                                </Descriptions.Item>
                                                                <Descriptions.Item label="Amount">
                                                                    <Text strong style={{ color: '#52c41a' }}>
                                                                        ${record.amount?.toFixed(2)}
                                                                    </Text>
                                                                </Descriptions.Item>
                                                                <Descriptions.Item label="Payment Method">
                                                                    <Tag
                                                                        color={getPaymentMethodColor(record.paymentMethod)}
                                                                        icon={getPaymentMethodIcon(record.paymentMethod)}
                                                                    >
                                                                        {record.paymentMethod}
                                                                    </Tag>
                                                                </Descriptions.Item>
                                                                <Descriptions.Item label="Status">
                                                                    <Badge
                                                                        status={record.status === 'Completed' ? 'success' : 'processing'}
                                                                        text={record.status}
                                                                    />
                                                                </Descriptions.Item>
                                                                {record.description && (
                                                                    <Descriptions.Item label="Description" span={2}>
                                                                        {record.description}
                                                                    </Descriptions.Item>
                                                                )}
                                                                {record.transactionId && (
                                                                    <Descriptions.Item label="Transaction ID" span={2}>
                                                                        <Text code>{record.transactionId}</Text>
                                                                    </Descriptions.Item>
                                                                )}
                                                            </Descriptions>
                                                        </div>
                                                    ),
                                                    okText: 'Close'
                                                });
                                            }}
                                        >
                                            View
                                        </Button>
                                    )
                                }
                            ]}
                            pagination={{
                                pageSize: 20,
                                showSizeChanger: true,
                                showQuickJumper: true
                            }}
                        />
                    ) : (
                        <div style={{ textAlign: 'center', padding: 40 }}>
                            <DollarOutlined style={{ fontSize: 48, color: '#d9d9d9', marginBottom: 16 }} />
                            <div style={{ color: '#666' }}>No payment history found</div>
                        </div>
                    )}
                </div>
            ),
            okText: 'Close'
        });
    };

    // CORRECTED: Always calculate from actual payments
    const calculatePaymentTotals = () => {
        const payments = lead?.advancePayments || paymentHistory;

        // Filter out duplicates and invalid payments
        const validPayments = payments.filter(payment =>
            payment &&
            payment.amount &&
            (payment.status === 'Completed' || payment.status === undefined) &&
            Number(payment.amount) > 0
        );

        // Calculate total from ALL completed payments
        const totalPaid = roundMoney(validPayments.reduce((sum, payment) => {
            return sum + Number(payment.amount);
        }, 0));

        const totalAmount = lead?.totalAmount || 0;
        const balance = getBalance(totalAmount, totalPaid);

        return {
            totalAmount,
            totalPaid,
            balance,
            paymentCount: validPayments.length
        };
    };

    const formatPaymentAmount = (amount) => (
        `$${Number(amount || 0).toLocaleString('en-US', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 2
        })}`
    );

    // Shared by the Payment Amount field's inline summary, its validator, and its
    // initial value below, so all three stay in sync from a single calculation.
    const getPaymentAmountBreakdown = () => {
        const payments = lead?.advancePayments || [];
        const uniqueMap = new Map();
        payments.forEach(payment => {
            const key = payment.transactionId || payment.id;
            if (!uniqueMap.has(key) ||
                new Date(payment.date) > new Date(uniqueMap.get(key).date)) {
                uniqueMap.set(key, payment);
            }
        });
        const uniquePayments = Array.from(uniqueMap.values());
        const totalPaid = roundMoney(uniquePayments.reduce((sum, p) =>
            p.status === 'Completed' ? sum + (Number(p.amount) || 0) : sum, 0));
        const totalAmount = lead?.totalAmount || 0;
        const balance = getBalance(totalAmount, totalPaid);
        return { totalAmount, totalPaid, balance };
    };

    const getSalesMadePaymentSummary = () => {
        const { totalAmount, totalPaid, balance } = calculatePaymentTotals();

        return {
            totalAmount,
            totalPaid,
            balance,
            // balance comes from getBalance in whole cents, so 0 means paid in full
            isReady: totalAmount > 0 && balance === 0
        };
    };

    const isAdvancePaymentCompleted = () => {
        const payments = lead?.advancePayments || paymentHistory || [];

        return lead?.leadStage === "Pending Payment" && payments.some(payment =>
            payment?.status === 'Completed' && Number(payment?.amount) > 0
        );
    };

    const validatePaymentFields = (values) => {
        const errors = [];

        if (!values.paymentAmount || values.paymentAmount <= 0) {
            errors.push('Payment amount is required');
        }

        if (!values.paymentMethod) {
            errors.push('Payment method is required');
        }

        // Validate based on payment method
        switch (values.paymentMethod) {
            case 'Card':
                if (!values.cardNumber) errors.push('Card number is required');
                if (!values.expirationDate) errors.push('Expiration date is required');
                if (!values.cvv) errors.push('CVV is required');
                if (!values.cardHolderName) errors.push('Card holder name is required');
                break;
                // case 'Payment Link':
                //     if (!values.paymentLink) errors.push('Payment link is required');
                //     break;
                // case 'Check Payment':
                //     if (!values.checkNumber) errors.push('Check number is required');
                //     if (!values.accountHolderName) errors.push('Account holder name is required');
                //     break;
                // case 'Zelle':
                //     if (!values.zelleContact) errors.push('Zelle email/phone is required');
                //     break;
                // case 'Wire Transfer':
                //     if (!values.bankName) errors.push('Bank name is required');
                //     if (!values.accountNumber) errors.push('Account number is required');
                //     if (!values.routingNumber) errors.push('Routing number is required');
                //     break;
                // case 'PayPal':
                //     if (!values.paypalEmail) errors.push('PayPal email is required');
                //     break;
                // case 'Cashapp':
                //     if (!values.cashappTag) errors.push('CashApp tag is required');
                //     break;
                // case 'Venmo':
                //     if (!values.venmoUsername) errors.push('Venmo username is required');
                //     break;
                // case 'Other':
                if (!values.description) errors.push('Payment details are required');
                break;
        }

        return errors;
    };

const syncZipCodeFromBilling = async (leadId, billingZip) => {
    // Only sync if billingZip exists and personalInfo.zipCode is empty/null
    if (billingZip && billingZip.trim() !== '') {
        try {
            // Check if personalInfo.zipCode is empty
            const hasPersonalZip = lead?.personalInfo?.zipCode && 
                                   lead.personalInfo.zipCode.trim() !== '';
            
            // If personalInfo zip is empty, sync from billing
            if (!hasPersonalZip) {
                console.log('Auto-syncing zip code from billing to personalInfo');
                
                const payload = {
                    personalInfo: {
                        ...lead.personalInfo,
                        zipCode: billingZip.trim()
                    }
                };
                
                const response = await API.UpdateLead(leadId, payload);
                
                if (response?.status) {
                    console.log('Zip code synced successfully');
                    return true;
                } else {
                    console.error('Failed to sync zip code:', response?.message);
                    return false;
                }
            }
        } catch (error) {
            console.error('Error syncing zip code:', error);
            return false;
        }
    }
    return false;
};
// Helper function to clean duplicate payments (run once)
const cleanDuplicatePayments = async () => {
    const existingPayments = lead?.advancePayments || [];
    
    // Deduplicate by transactionId
    const uniqueMap = new Map();
    existingPayments.forEach(payment => {
        const key = payment.transactionId || payment.id;
        if (!uniqueMap.has(key) || 
            new Date(payment.date) > new Date(uniqueMap.get(key).date)) {
            uniqueMap.set(key, payment);
        }
    });
    
    const cleanPayments = Array.from(uniqueMap.values());
    
    if (cleanPayments.length !== existingPayments.length) {
        console.log(`Cleaning duplicates: ${existingPayments.length} → ${cleanPayments.length}`);
        
        // Update lead with clean payments
        const response = await API.UpdateLead(lead._id, {
            advancePayments: cleanPayments
        });
        
        if (response?.status) {
            message.success('Duplicate payments cleaned up');
            onUpdate(response.data);
        }
    }
};

const handleSubmit = async (values) => {
    let payload = {};

    try {
        setLoading(true);

        console.log('Form values:', values);
        console.log('Current lead:', lead);

        // Validate that a new stage is selected (different from current)
        if (!values.leadStage) {
            message.error('Please select a new lead stage');
            return;
        }

        

        // Validate if the selected stage is allowed based on rules
        if (availableStages.length > 0 && !availableStages.includes(values.leadStage)) {
            message.error(`Cannot change from "${currentStage}" to "${values.leadStage}". Please select a valid stage from the dropdown.`);
            return;
        }

        // Validate quote amount for Quoted stage only
        if (values.leadStage === "Quoted" && (!values.totalAmount || values.totalAmount <= 0)) {
            message.error('Please enter a valid quote amount for Quoted stage');
            return;
        }

        // Validate payment amount for Payment Details stage
        if (values.leadStage === "Payment Details" && (!values.paymentAmount || values.paymentAmount <= 0)) {
            message.error('Please enter a valid payment amount for Payment Details stage');
            return;
        }

        // Validate comments for stages that require them
        if (stagesRequiringMandatoryComments.includes(values.leadStage) && !values.comments) {
            message.error(`Comments are required for ${values.leadStage} stage`);
            return;
        }

        if (values.leadStage === "Sales Made" && !isSalesMadeAllowed()) {
            const { totalAmount, totalPaid, balance } = getSalesMadePaymentSummary();
            message.error(
                `Full payment is required before Sales Made. Total: ${formatPaymentAmount(totalAmount)} | Paid: ${formatPaymentAmount(totalPaid)} | Balance: ${formatPaymentAmount(balance)}`
            );
            return;
        }

        // Define payment methods that do NOT require billing address
        const noBillingMethods = [
                'Cashapp',
                'Venmo',
                'PayPal',
                'Pay Tomorrow',
                'ACH',
                'Zelle',
                'Cash',
                'Check Payment',
                'Wire Transfer',
                
        ];
        const requiresBillingAddress = !noBillingMethods.includes(values.paymentMethod);
        // "Same as billing" only applies when a billing address is collected
        if (!requiresBillingAddress && values.sameAsBilling) {
            values = { ...values, sameAsBilling: false };
        }

        // ========== VALIDATE ADDRESSES BASED ON PAYMENT METHOD ==========
        const isAddressRequiredPaymentStage = values.leadStage === "Payment Details" || values.leadStage === "Pending Payment";
        if (isAddressRequiredPaymentStage) {
            // Validate billing address only if required
            if (requiresBillingAddress) {
                if (!values.billingStreet || values.billingStreet.trim() === '') {
                    message.error('Billing street address is required for this payment method');
                    setLoading(false);
                    return;
                }
                if (!values.billingCity || values.billingCity.trim() === '') {
                    message.error('Billing city is required for this payment method');
                    setLoading(false);
                    return;
                }
                if (!values.billingState || values.billingState.trim() === '') {
                    message.error('Billing state is required for this payment method');
                    setLoading(false);
                    return;
                }
                if (!values.billingZip || values.billingZip.trim() === '') {
                    message.error('Billing ZIP code is required for this payment method');
                    setLoading(false);
                    return;
                }
            }

            // Validate shipping address (always required unless sameAsBilling is checked and billing exists)
            const sameAsBilling = values.sameAsBilling;
            
            if (!sameAsBilling) {
                if (!values.shippingStreet || values.shippingStreet.trim() === '') {
                    message.error('Shipping street address is required');
                    setLoading(false);
                    return;
                }
                if (!values.shippingCity || values.shippingCity.trim() === '') {
                    message.error('Shipping city is required');
                    setLoading(false);
                    return;
                }
                if (!values.shippingState || values.shippingState.trim() === '') {
                    message.error('Shipping state is required');
                    setLoading(false);
                    return;
                }
                if (!values.shippingZip || values.shippingZip.trim() === '') {
                    message.error('Shipping ZIP code is required');
                    setLoading(false);
                    return;
                }
            } else {
                // If sameAsBilling is checked, ensure billing address exists
                if (!values.billingStreet || !values.billingCity || !values.billingState || !values.billingZip) {
                    message.error('Billing address must be filled before checking "Same as billing"');
                    setLoading(false);
                    return;
                }
            }
        }

// ============ ADVANCE PAYMENT LOGIC ============
if (values.leadStage === "Pending Payment" && values.paymentAmount) {
    console.log('Processing advance payment...');
    
    // Validate payment method fields
    const paymentErrors = validatePaymentFields(values);
    if (paymentErrors.length > 0) {
        message.error(`Payment validation failed: ${paymentErrors.join(', ')}`);
        setLoading(false);
        return;
    }
    
    // Get existing payments
    const existingPayments = lead?.advancePayments || [];
    
    // Calculate current total paid from existing payments
    const currentTotalPaid = roundMoney(existingPayments.reduce((sum, payment) => {
        if (payment.status === 'Completed') {
            return sum + (Number(payment.amount) || 0);
        }
        return sum;
    }, 0));

    const newPaymentAmount = roundMoney(values.paymentAmount);
    const totalAmount = lead?.totalAmount || 0;

    // Check if this payment would make the total paid >= total amount (FULL PAYMENT)
    const wouldBeFullyPaid = totalAmount > 0 && getBalance(totalAmount, currentTotalPaid + newPaymentAmount) === 0;
    
    // If full payment detected - DON'T SAVE, show alert message
    if (wouldBeFullyPaid) {
        setFullPaymentAlert(true);
        // Auto-hide alert after 5 seconds
        setTimeout(() => {
            setFullPaymentAlert(false);
        }, 5000);
        setLoading(false);
        return; // STOP - Don't save the payment
    }
    
    // For partial advance payment, check if exceeding total
    if (totalAmount > 0 && (currentTotalPaid + newPaymentAmount) > totalAmount) {
        const overpaymentAmount = (currentTotalPaid + newPaymentAmount) - totalAmount;
        message.warning({
            content: (
                <div>
                    <strong>⚠️ Overpayment Warning!</strong>
                    <div style={{ marginTop: '4px', fontSize: '12px' }}>
                        This payment would exceed the total amount by <strong>${overpaymentAmount.toFixed(2)}</strong>.
                    </div>
                </div>
            ),
            duration: 5
        });
        setLoading(false);
        return; // STOP - Don't save overpayment
    }
    
    // ========== ONLY PARTIAL PAYMENTS REACH HERE ==========
    const recordAdvancePayment = async () => {
        // Generate UNIQUE IDs for payment
        const generateUniqueId = () => {
            const timestamp = Date.now();
            const randomPart1 = Math.random().toString(36).substring(2, 10);
            const randomPart2 = Math.random().toString(36).substring(2, 6);
            const counter = Math.floor(Math.random() * 10000);
            return `PAY-${timestamp}-${randomPart1}-${randomPart2}-${counter}`;
        };
        
        const generateUniqueTransactionId = () => {
            const timestamp = Date.now();
            const randomPart = Math.random().toString(36).substring(2, 15);
            const counter = Math.floor(Math.random() * 10000);
            const perfTime = performance.now().toString(36).replace('.', '');
            return `TXN-${timestamp}-${randomPart}-${perfTime}-${counter}`;
        };
        
        // Check for duplicate within last 2 minutes
        const now = new Date();
        let duplicateFound = false;
        
        duplicateFound = existingPayments.some(payment => {
            const paymentTime = new Date(payment.date);
            const timeDiff = Math.abs(now - paymentTime) / (1000 * 60);
            const isSameAmount = Math.abs(payment.amount - newPaymentAmount) < 0.01;
            const isSameMethod = payment.paymentMethod === values.paymentMethod;
            return isSameAmount && isSameMethod && timeDiff < 2;
        });
        
        if (duplicateFound) {
            message.warning('Duplicate payment detected. Please wait a moment before trying again.');
            setLoading(false);
            return;
        }
        
        const userDataString = localStorage.getItem('userData');
        const userData = userDataString ? JSON.parse(userDataString) : null;
        
        // Create NEW payment with UNIQUE IDs
        const newPayment = {
            id: generateUniqueId(),
            amount: newPaymentAmount,
            paymentMethod: values.paymentMethod,
            status: "Completed",
            createdBy: userData?._id || userData?.id,
            date: new Date().toISOString(),
            transactionId: generateUniqueTransactionId(),
            currency: "USD",
            exchangeRate: 1,
            description: values.description || "",
            paymentType: "Advance"
        };
        
        // Add payment method specific details
        switch (values.paymentMethod) {
            case 'Card':
                newPayment.cardNumber = values.cardNumber?.replace(/\s/g, '');
                newPayment.expirationDate = values.expirationDate;
                newPayment.cardHolderName = values.cardHolderName;
                if (values.cardNumber) {
                    // Defensive re-derivation: never trust a possibly-stale values.cardType —
                    // recompute it from the card number actually being submitted (the backend
                    // is still authoritative and will also derive it independently).
                    const cleaned = values.cardNumber.replace(/\s/g, '');
                    newPayment.cardType = detectCardType(cleaned);
                    newPayment.lastFourDigits = cleaned.slice(-4);
                } else {
                    newPayment.cardType = values.cardType;
                }
                break;
            default:
                break;
        }
        
        // Append new payment to existing payments
        const updatedPayments = [...existingPayments, newPayment];
        
        // Calculate totals from ALL completed payments
        const totalPaid = roundMoney(updatedPayments.reduce((sum, payment) => {
            if (payment.status === 'Completed') {
                return sum + (Number(payment.amount) || 0);
            }
            return sum;
        }, 0));

        const newBalance = getBalance(totalAmount, totalPaid);

        // Prepare payload for partial payment
        let advancePayload = {
            leadStage: "Pending Payment",
            bussinessName: values.businessName,
            customerName: lead?.personalInfo?.fullName || '',
            attnName: values.sameAsBilling ? '' : (values.attnName || '').trim(),
            advancePayments: updatedPayments,
            comment: values.comments || "Advance payment recorded",
            paymentStatus: "Partially Paid"
        };
        
        // Match Payment Details billing/shipping address persistence.
        if (requiresBillingAddress || values.sameAsBilling) {
            advancePayload.billingAddress = {
                street: values.billingStreet || '',
                city: values.billingCity || '',
                state: values.billingState || '',
                zip: values.billingZip || ''
            };
        }
        
        if (values.sameAsBilling) {
            advancePayload.shippingAddress = {
                street: values.billingStreet || '',
                city: values.billingCity || '',
                state: values.billingState || '',
                zip: values.billingZip || ''
            };
        } else {
            advancePayload.shippingAddress = {
                street: values.shippingStreet || '',
                city: values.shippingCity || '',
                state: values.shippingState || '',
                zip: values.shippingZip || ''
            };
        }
        
        // Add warranty if provided
        if (values.warranty) {
            advancePayload.warranty = values.warranty;
        }
        
        console.log('📊 Partial Pending Payment Summary:', {
            newPaymentAmount,
            totalPaid,
            newBalance,
            finalStage: advancePayload.leadStage
        });
        
        // Make the API call
        const response = await API.UpdateLead(lead._id, advancePayload);
        
        if (response?.status) {
            message.success(`Advance payment of $${newPaymentAmount.toFixed(2)} recorded successfully. Total paid: $${totalPaid.toFixed(2)}. Balance: $${newBalance.toFixed(2)}`);
            onUpdate(response.data);
            resetForm();
            onClose();
        } else {
            message.error(response?.message || 'Failed to record advance payment');
        }
    };
    
    // Only proceed with partial payment
    await recordAdvancePayment();
}
        // ========== SYNC ZIP CODE FROM BILLING TO PERSONALINFO ==========
        if (values.billingZip && values.billingZip.trim() !== '') {
            const hasPersonalZip = lead?.personalInfo?.zipCode && 
                                   lead.personalInfo.zipCode.trim() !== '';
            
            if (!hasPersonalZip) {
                try {
                    console.log('Auto-syncing zip code from billing to personalInfo');
                    
                    const syncPayload = {
                        personalInfo: {
                            ...lead.personalInfo,
                            zipCode: values.billingZip.trim()
                        }
                    };
                    
                    const syncResponse = await API.UpdateLead(lead._id, syncPayload);
                    
                    if (syncResponse?.status) {
                        console.log('Zip code synced successfully');
                    } else {
                        console.error('Failed to sync zip code:', syncResponse?.message);
                    }
                } catch (error) {
                    console.error('Error syncing zip code:', error);
                }
            }
        }

        // ✅ DECLARE payload HERE - outside all conditional blocks
        payload = {
            leadStage: values.leadStage,
            bussinessName: values.businessName,
            customerName: lead?.personalInfo?.fullName || '',
        };

        // ATTN Name field only exists on the Payment Details / Pending Payment screens.
        // Other stages (Sales Made, Follow-Up, ...) must not touch the saved value.
        if (isAddressRequiredPaymentStage) {
            payload.attnName = values.sameAsBilling ? '' : (values.attnName || '').trim();
        }

        // Add billing address to payload ONLY if it's required (Card, Other, etc.)
        if (requiresBillingAddress || values.sameAsBilling) {
            payload.billingAddress = {
                street: values.billingStreet || '',
                city: values.billingCity || '',
                state: values.billingState || '',
                zip: values.billingZip || ''
            };
        }

        // Add warranty if provided
        if (values.warranty) {
            payload.warranty = values.warranty;
        }

        // Add shipping address
        if (values.sameAsBilling) {
            // If sameAsBilling is checked, copy billing address to shipping
            payload.shippingAddress = {
                street: values.billingStreet || '',
                city: values.billingCity || '',
                state: values.billingState || '',
                zip: values.billingZip || ''
            };
        } else {
            // Otherwise use the shipping address values
            payload.shippingAddress = {
                street: values.shippingStreet || '',
                city: values.shippingCity || '',
                state: values.shippingState || '',
                zip: values.shippingZip || ''
            };
        }

        // Add follow-up date if "Follow-Up" stage is selected
        if (values.leadStage === "Follow-Up" && values.followUpDate) {
            const pstDateTime = dayjs(values.followUpDate).tz('America/Los_Angeles', true);
            payload.followUpDate = pstDateTime.toISOString();
        }

        // Add quote amount ONLY for Quoted stage
        if (values.leadStage === "Quoted" && values.totalAmount) {
            // Only the new quote is sent; the server appends it to the full quote history.
            payload.quotedValue = [{ price: values.totalAmount }];
            payload.totalAmount = values.totalAmount;
        }

   if (values.leadStage === "Sales Made") {
    if (!values.orderNumber || values.orderNumber.trim() === '') {
        message.error('Order number is required for Sales Made stage');
        setLoading(false);
        return;
    }
    
    if (!TYPE_OF_LEAD_OPTIONS.includes(values.typeOfLead)) {
        message.error('Please select Type Of Lead');
        setLoading(false);
        return;
    }
    
    // ✅ VALIDATE MILES PROMISE CUSTOMER
    if (!values.milesPromiseCustomer || values.milesPromiseCustomer.trim() === '') {
        message.error('Miles Promise Customer comment is required for Sales Made stage');
        setLoading(false);
        return;
    }

    const trackingPromiseDays = normalizeTrackingPromiseDaysInput(values.trackingPromiseDays);

    if (!isValidTrackingPromiseDays(trackingPromiseDays)) {
        message.error(TRACKING_PROMISE_DAYS_ERROR);
        setLoading(false);
        return;
    }
    
    if (values.orderNumber && values.orderNumber.trim() !== '') {
        payload.orderNumber = values.orderNumber.trim();
    }
    
    payload.typeOfLead = values.typeOfLead;
    
    // ✅ ADD MILES PROMISE CUSTOMER TO PAYLOAD
    if (values.milesPromiseCustomer && values.milesPromiseCustomer.trim() !== '') {
        payload.comment = values.milesPromiseCustomer.trim();
    }

    payload.trackingPromiseDays = trackingPromiseDays;
    
    // Set orderDate to the selected sales made date
    if (values.salesMadeDate) {
        const salesMadeDateUTC = dayjs(values.salesMadeDate)
            .tz('America/Los_Angeles')
            .hour(0)
            .minute(0)
            .second(0)
            .millisecond(0)
            .toISOString();
        
        payload.orderDate = salesMadeDateUTC;
    }
    
    // Add to leadStageHistory with the selected salesMadeDate
    const existingHistory = lead?.leadStageHistory || [];
    const existingSalesMadeEntry = existingHistory.find(
        stage => stage.stage === "Sales Made"
    );
    
    const salesMadeChangedAt = values.salesMadeDate 
        ? dayjs(values.salesMadeDate)
            .tz('America/Los_Angeles')
            .hour(0)
            .minute(0)
            .second(0)
            .millisecond(0)
            .toISOString()
        : new Date().toISOString();
    
    if (!existingSalesMadeEntry) {
        payload.leadStageHistory = [
            ...existingHistory,
            {
                stage: "Sales Made",
                changedAt: salesMadeChangedAt,
                comment: values.milesPromiseCustomer || values.comments || "Stage changed to Sales Made"
            }
        ];
    } else {
        payload.leadStageHistory = existingHistory.map(entry => 
            entry.stage === "Sales Made" 
                ? { ...entry, changedAt: salesMadeChangedAt, comment: values.milesPromiseCustomer || values.comments || entry.comment }
                : entry
        );
    }
}

        // ============ PAYMENT DETAILS LOGIC (COMPLETELY FIXED) ============
        if (values.leadStage === "Payment Details" && values.paymentAmount) {
            console.log('Processing payment...');
            
            // Validate payment method fields
            const paymentErrors = validatePaymentFields(values);
            if (paymentErrors.length > 0) {
                message.error(`Payment validation failed: ${paymentErrors.join(', ')}`);
                setLoading(false);
                return;
            }
            
            // Get existing payments
            const existingPayments = lead?.advancePayments || [];
            
            // Calculate current total paid from existing payments
            const currentTotalPaid = roundMoney(existingPayments.reduce((sum, payment) => {
                if (payment.status === 'Completed') {
                    return sum + (Number(payment.amount) || 0);
                }
                return sum;
            }, 0));

            const newPaymentAmount = roundMoney(values.paymentAmount);
            const totalAmount = lead?.totalAmount || 0;
            const potentialNewTotal = roundMoney(currentTotalPaid + newPaymentAmount);

            // Check for overpayment
            if (totalAmount > 0 && toCents(potentialNewTotal) > toCents(totalAmount)) {
                const remainingAmount = getBalance(totalAmount, currentTotalPaid);
                message.error({
                    content: (
                        <div>
                            <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>
                                ❌ Payment Would Exceed Total Amount
                            </div>
                            <div>Total Amount: <strong>${totalAmount.toFixed(2)}</strong></div>
                            <div>Already Paid: <strong>${currentTotalPaid.toFixed(2)}</strong></div>
                            <div>Remaining Balance: <strong>${remainingAmount.toFixed(2)}</strong></div>
                            <div style={{ marginTop: '8px', color: '#ff4d4f' }}>
                                You are trying to pay: ${newPaymentAmount.toFixed(2)}
                            </div>
                        </div>
                    ),
                    duration: 6
                });
                setLoading(false);
                return;
            }
            
            // Generate UNIQUE IDs for payment
            const generateUniqueId = () => {
                const timestamp = Date.now();
                const randomPart1 = Math.random().toString(36).substring(2, 10);
                const randomPart2 = Math.random().toString(36).substring(2, 6);
                const counter = Math.floor(Math.random() * 10000);
                return `PAY-${timestamp}-${randomPart1}-${randomPart2}-${counter}`;
            };
            
            const generateUniqueTransactionId = () => {
                const timestamp = Date.now();
                const randomPart = Math.random().toString(36).substring(2, 15);
                const counter = Math.floor(Math.random() * 10000);
                const perfTime = performance.now().toString(36).replace('.', '');
                return `TXN-${timestamp}-${randomPart}-${perfTime}-${counter}`;
            };
            
            // Check for duplicate within last 2 minutes
            const now = new Date();
            let duplicateFound = false;
            
            duplicateFound = existingPayments.some(payment => {
                const paymentTime = new Date(payment.date);
                const timeDiff = Math.abs(now - paymentTime) / (1000 * 60);
                const isSameAmount = Math.abs(payment.amount - newPaymentAmount) < 0.01;
                const isSameMethod = payment.paymentMethod === values.paymentMethod;
                return isSameAmount && isSameMethod && timeDiff < 2;
            });
            
            if (duplicateFound) {
                message.warning('Duplicate payment detected. Please wait a moment before trying again.');
                setLoading(false);
                return;
            }
            
            const userDataString = localStorage.getItem('userData');
            const userData = userDataString ? JSON.parse(userDataString) : null;
            
            // Create NEW payment with UNIQUE IDs
            const newPayment = {
                id: generateUniqueId(),
                amount: newPaymentAmount,
                paymentMethod: values.paymentMethod,
                status: "Completed",
                createdBy: userData?._id || userData?.id,
                date: new Date().toISOString(),
                transactionId: generateUniqueTransactionId(),
                currency: "USD",
                exchangeRate: 1,
                description: values.description || ""
            };
            
            // Add payment method specific details
            switch (values.paymentMethod) {
                case 'Card':
                    newPayment.cardNumber = values.cardNumber?.replace(/\s/g, '');
                    newPayment.expirationDate = values.expirationDate;
                    newPayment.cardHolderName = values.cardHolderName;
                    if (values.cardNumber) {
                        // Defensive re-derivation: never trust a possibly-stale values.cardType —
                        // recompute it from the card number actually being submitted (the backend
                        // is still authoritative and will also derive it independently).
                        const cleaned = values.cardNumber.replace(/\s/g, '');
                        newPayment.cardType = detectCardType(cleaned);
                        newPayment.lastFourDigits = cleaned.slice(-4);
                    } else {
                        newPayment.cardType = values.cardType;
                    }
                    break;
                case 'Payment Link':
                    newPayment.paymentLink = values.paymentLink;
                    newPayment.linkExpiryDate = values.linkExpiryDate;
                    newPayment.paymentGateway = values.paymentGateway;
                    break;
                case 'Check Payment':
                    newPayment.checkNumber = values.checkNumber;
                    newPayment.accountHolderName = values.accountHolderName;
                    newPayment.checkDate = values.checkDate;
                    break;
                case 'Zelle':
                    newPayment.zelleEmail = values.zelleEmail;
                    newPayment.zellePhone = values.zellePhone;
                    newPayment.senderName = values.senderName;
                    break;
                case 'Cashapp':
                    newPayment.cashappTag = values.cashappTag;
                    break;
                case 'Venmo':
                    newPayment.venmoUsername = values.venmoUsername;
                    break;
                case 'Wire Transfer':
                    newPayment.bankName = values.bankName;
                    newPayment.accountNumber = values.accountNumber;
                    newPayment.routingNumber = values.routingNumber;
                    newPayment.wireReference = values.wireReference;
                    break;
                case 'PayPal':
                    newPayment.paypalEmail = values.paypalEmail;
                    newPayment.paypalTransactionId = values.paypalTransactionId;
                    break;
                case 'Pay Tomorrow':
                    newPayment.promiseToPayDate = values.promiseToPayDate;
                    newPayment.promiseAmount = values.promiseAmount;
                    newPayment.paymentTerms = values.paymentTerms;
                    newPayment.followUpDate = values.followUpDate;
                    break;
                // case 'Other':
                //     newPayment.otherMethodName = values.otherMethodName;
                //     newPayment.referenceNumber = values.referenceNumber;
                //     newPayment.notes = values.notes;
                //     break;
            }
            
            // Append new payment to existing payments
            const updatedPayments = [...existingPayments, newPayment];
            
            // Calculate totals from ALL completed payments
            const totalPaid = roundMoney(updatedPayments.reduce((sum, payment) => {
                if (payment.status === 'Completed') {
                    return sum + (Number(payment.amount) || 0);
                }
                return sum;
            }, 0));

            const newBalance = getBalance(totalAmount, totalPaid);

            // Update payload with payment details
            payload.advancePayments = updatedPayments;
            
            // Add total amount if not set
            if (!lead?.totalAmount && values.totalAmount) {
                payload.totalAmount = values.totalAmount;
            }
            
            // Add comment
            if (values.comments) {
                payload.comment = values.comments;
            }
            
            // Determine final stage based on payment status
            if (newBalance <= 0) {
                payload.paymentStatus = "Fully Paid";
                payload.leadStage = "Payment Completed";
            } else if (totalPaid > 0) {
                payload.paymentStatus = "Partially Paid";
                payload.leadStage = "Pending Payment";
            } else {
                payload.paymentStatus = "Not Paid";
            }

            // A balance top-up on a re-quoted Sales Made order keeps it in Sales Made
            if (currentStage === "Sales Made") {
                payload.leadStage = "Sales Made";
            }

            console.log('📊 Payment Summary:', {
                existingCount: existingPayments.length,
                newAmount: newPaymentAmount,
                newPaymentId: newPayment.id,
                newTransactionId: newPayment.transactionId,
                totalPaidBefore: currentTotalPaid,
                totalPaidAfter: totalPaid,
                newBalance: newBalance,
                finalStage: payload.leadStage,
                totalPayments: updatedPayments.length,
                paymentAmounts: updatedPayments.map(p => p.amount)
            });
            
            console.log('Sending payload to backend:', JSON.stringify(payload, null, 2));
            
            // Make the API call
            const response = await API.UpdateLead(lead._id, payload);
            
            if (response?.status) {
                message.success(`Payment of $${newPaymentAmount.toFixed(2)} recorded successfully. Total paid: $${totalPaid.toFixed(2)}. Balance: $${newBalance.toFixed(2)}`);
                onUpdate(response.data);
                resetForm();
                onClose();
            } else {
                message.error(response?.message || 'Failed to update lead');
            }
        } else {
            // For non-payment stages
            if (values.comments) {
                payload.comment = values.comments;
            }
            
            console.log('Complete Payload to Backend:', JSON.stringify(payload, null, 2));
             
            const response = await API.UpdateLead(lead._id, payload);
             
            if (response?.status) {
                message.success('Lead updated successfully!');
                onUpdate(response.data);
                resetForm();
                onClose();
            } else {
                message.error(
                    payload.trackingPromiseDays !== undefined
                        ? getTrackingPromiseDaysErrorMessage(response?.message)
                        : response?.message || 'Failed to update lead'
                );
            }
        }
    } catch (error) {
        console.error('Error updating lead:', error);
        const fallbackError = error.response?.data?.message || error.message;
        message.error(
            payload.trackingPromiseDays !== undefined
                ? getTrackingPromiseDaysErrorMessage(fallbackError)
                : 'Failed to update lead: ' + fallbackError
        );
    } finally {
        setLoading(false);
    }
};

    const isSalesMadeAllowed = () => {
        return getSalesMadePaymentSummary().isReady;
    };
const handleSubmitFailed = ({ errorFields = [] }) => {
    const hasTypeOfLeadError = errorFields.some(({ name = [] }) => name.includes('typeOfLead'));

    if (hasTypeOfLeadError) {
        message.error('Please select Type Of Lead');
    }
};

const renderStageOptions = () => {
    if (availableStages.length === 0) {
        return [
            <Option key="no-options" value="no-options" disabled>
                No stage transitions available from {currentStage}
            </Option>
        ];
    }

    return availableStages.map(stage => {
        const requiresQuote = stage === "Quoted";
        const requiresPayment = stage === "Payment Details";
        // Map "Pending Payment" to show as "Advance Payment" in dropdown
        const isAdvancePayment = stage === "Pending Payment";
        const isSalesMade = stage === "Sales Made";
        
        // Get display name for dropdown
        const getDisplayName = (stageName) => {
            if (stageName === "Pending Payment") return "Advance Payment";
            return stageName;
        };
        
        // ✅ NEW: Disable Payment Details if lead is fully paid
        const { totalAmount, totalPaid, balance } = calculatePaymentTotals();
        const isFullyPaid = totalAmount > 0 && balance === 0;
        const salesMadePaymentSummary = isSalesMade ? getSalesMadePaymentSummary() : null;
        
        let isDisabled = false;
        let disabledReason = "";
        let missingFields = [];

        // ✅ Disable Payment Details if fully paid
        if (stage === "Payment Details" && isFullyPaid) {
            isDisabled = true;
            disabledReason = "Lead is fully paid - cannot go to Payment Details";
        }

        if (isSalesMade) {
            missingFields = validateRequiredFieldsForSalesMade();
            
            if (!salesMadePaymentSummary.isReady) {
                isDisabled = true;
                disabledReason = salesMadePaymentSummary.totalAmount <= 0
                    ? "Total amount required"
                    : `Payment required. Balance: ${formatPaymentAmount(salesMadePaymentSummary.balance)}`;
            } else if (missingFields.length > 0) {
                disabledReason = `Missing: ${missingFields.slice(0, 2).join(', ')}${missingFields.length > 2 ? '...' : ''}`;
            }
        }

        if (isSalesMade) {
            return (
                <Option
                    key={stage}
                    value={stage}
                    disabled={isDisabled}
                    label={getDisplayName(stage)}
                    title={disabledReason}
                >
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, padding: '2px 0' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                            <span>{getDisplayName(stage)}</span>
                            <Tag
                                color={salesMadePaymentSummary.isReady ? 'green' : 'orange'}
                                size="small"
                                style={{ margin: 0 }}
                            >
                                {salesMadePaymentSummary.isReady ? '✓ Ready' : 'Payment Required'}
                            </Tag>
                        </div>
                        <Text type="secondary" style={{ fontSize: 11, lineHeight: '16px' }}>
                            Total: {formatPaymentAmount(salesMadePaymentSummary.totalAmount)} | Paid: {formatPaymentAmount(salesMadePaymentSummary.totalPaid)} | Balance: {formatPaymentAmount(salesMadePaymentSummary.balance)}
                        </Text>
                    </div>
                </Option>
            );
        }

        return (
            <Option
                key={stage}
                value={stage}  // Value remains "Pending Payment" for internal use
                disabled={isDisabled}
                 label={getDisplayName(stage)} 
                title={isDisabled && missingFields.length > 0 ? 
                    `Missing required fields. Click "Go to Details Tab" in the error modal.\n${missingFields.map(f => `• ${f}`).join('\n')}` : 
                    disabledReason}
            >
                <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                    <span>{getDisplayName(stage)}</span>  {/* Shows "Advance Payment" for Pending Payment */}
                    <Space>
                        {requiresQuote && (
                            <Tag color="green" size="small">
                                Quote Required
                            </Tag>
                        )}
                        {requiresPayment && (
                            <Tag color="blue" size="small">
                                Payment Required
                            </Tag>
                        )}
                       
                        {disabledReason}
                        {isSalesMade && isDisabled && (
                            <Tag 
                                color="red" 
                                size="small"
                                style={{ cursor: 'pointer' }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    if (missingFields.length > 0 && onTabChange) {
                                        onTabChange('details');
                                        onClose();
                                    }
                                }}
                            >
                                Click here to enter the Missing values
                            </Tag>
                        )}
                        {isSalesMade && !isDisabled && (
                            <Tag color="green" size="small">
                                ✓ Ready
                            </Tag>
                        )}
                    </Space>
                </Space>
            </Option>
        );
    });
};

    // Check if stage is editable (has available transitions)
    const isStageEditable = availableStages.length > 0;

    return (
        <Drawer
            title={
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <EditOutlined />
                    <span>Edit Lead</span>
                </div>
            }
            width={700}
            onClose={handleCancel}
            open={visible}
            bodyStyle={{ paddingBottom: 80 }}
            extra={
                <Space>
                    <Button onClick={handleCancel} icon={<CloseOutlined />}>
                        Cancel
                    </Button>
                    {isStageEditable && (
                        <Button
                            onClick={() => form.submit()}
                            type="primary"
                            icon={<SaveOutlined />}
                            loading={loading}
                            disabled={!selectedStage || (selectedStage === "Sales Made" && !isSalesMadeAllowed())}
                        >
                            Save Changes
                        </Button>
                    )}
                </Space>
            }
        >
            <Row gutter={[24, 24]}>
                <Col xs={24}>
                    <Card size="small">
                        <Form
                            form={form}
                            layout="vertical"
                            onFinish={handleSubmit}
                            onFinishFailed={handleSubmitFailed}
                            onValuesChange={() => setHasMadeChanges(true)}
                        >
                       <Form.Item label="Current Stage">
    <div style={{ padding: '8px 0' }}>
        <Tag
            color={stageColorMap[currentStage]}
            style={{ fontSize: '14px', padding: '4px 12px' }}
        >
            {currentStage === "Payment Completed" ? "Payment Details Received" : 
             currentStage === "Pending Payment" ? "Advance Payment" : 
             currentStage}
        </Tag>
        <Text type="secondary" style={{ marginLeft: 12 }}>
            <FieldTimeOutlined /> Last updated: {dayjs(lead?.updatedAt).format('MMM DD, YYYY hh:mm A')}
        </Text>
    </div>
</Form.Item>

                            {/* Warning if no transitions available */}
                            {!isStageEditable ? (
                                <Alert
                                    message="Stage Cannot Be Changed"
                                    description={`No stage transitions available from "${currentStage}" stage.`}
                                    type="warning"
                                    showIcon
                                    style={{ marginBottom: 16 }}
                                />
                            ) : (
                                <>
                                    {/* Information Message */}


                                    {/* Stage Selection */}
                        <Form.Item
    name="leadStage"
    label="Change Stage To"
    rules={[{ required: true, message: 'Please select a stage' }]}
    tooltip={`Select new stage based on transition rules for "${currentStage}"`}
>
    <Select
        placeholder="Select new stage"
        onChange={handleStageChange}
        style={{ width: '100%' }}
        size="large"
        showSearch
        // Add this to format the selected value display
        optionLabelProp="label"
        filterOption={(input, option) => {
            if (option.props.disabled) return false;
            const optionText = option.props.label?.toLowerCase() ||
                option.props.children?.props?.children?.[0]?.toLowerCase() ||
                option.props.children?.toLowerCase() ||
                '';
            return optionText.includes(input.toLowerCase());
        }}
    >
        {renderStageOptions()}
    </Select>
</Form.Item>

{selectedStage && selectedStage !== currentStage && (
    <>
        {/* Warranty Field - Show only for Payment Details and Sales Made stages */}
        {(selectedStage === "Payment Details" || selectedStage === "Sales Made" || selectedStage === "Pending Payment") && (
            <Form.Item
                name="warranty"
                label={
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <CheckCircleOutlined style={{ color: '#52c41a' }} />
                        <span>Warranty</span>
                        <Tag color="blue" style={{ marginLeft: 'auto' }}>
                            Optional
                        </Tag>
                    </div>
                }
                initialValue={lead?.warranty}
                tooltip="Select or enter warranty period for this lead (optional)"
            >
                <AutoComplete
                    placeholder="Select or enter warranty (optional)"
                    allowClear
                    options={warrantyOptions}
                    style={{ width: '100%' }}
                    onChange={() => setHasMadeChanges(true)}
                    filterOption={(inputValue, option) =>
                        option?.label?.toLowerCase().includes(inputValue.toLowerCase())
                    }
                />
            </Form.Item>
        )}

        {/* Divider for visual separation - only show if warranty field was shown */}
        {(selectedStage === "Payment Details" || selectedStage === "Sales Made" || selectedStage === "Pending Payment") && (
            <Divider style={{ margin: '12px 0' }} />
        )}
    </>
)}
                                    {/* Show quote amount field ONLY for Quoted stage */}
                                    
                                    {selectedStage && selectedStage === "Quoted" && (
                                        <Form.Item
                                            name="totalAmount"
                                            label={
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                    <DollarOutlined style={{ color: '#52c41a' }} />
                                                    <span>Quote Amount</span>
                                                    <Tag color="green" style={{ marginLeft: 'auto' }}>
                                                        Required
                                                    </Tag>
                                                </div>
                                            }
                                            rules={[
                                                { required: true, message: 'Quote amount is required for Quoted stage' },
                                                {
                                                    validator: (_, value) => {
                                                        if (value === undefined || value === null || value === 0) {
                                                            return Promise.reject(new Error('Amount must be greater than 0'));
                                                        }
                                                        if (value < 0) {
                                                            return Promise.reject(new Error('Amount cannot be negative'));
                                                        }
                                                        if (value < 1) {
                                                            return Promise.reject(new Error('Amount must be at least $1.00'));
                                                        }
                                                        return Promise.resolve();
                                                    }
                                                }
                                            ]}
                                            initialValue={quoteAmount}
                                            tooltip="Enter the quoted amount for the customer"
                                        >
                                            <InputNumber
                                                placeholder="Enter quote amount"
                                                style={{ width: '100%' }}
                                                min={1}
                                                step={0.01}
                                                formatter={(value) =>
                                                    `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                                                }
                                                parser={(value) =>
                                                    value.replace(/\$\s?|(,*)/g, '')
                                                }
                                                size="large"
                                                onChange={(value) => {
                                                    setQuoteAmount(value || 0);
                                                    setHasMadeChanges(true);
                                                }}
                                                onKeyDown={(e) => {
                                                    if (e.key === '-' || e.key === '–' || e.key === '—') {
                                                        e.preventDefault();
                                                    }
                                                }}
                                                onPaste={(e) => {
                                                    const pastedText = e.clipboardData.getData('text');
                                                    if (pastedText.includes('-')) {
                                                        e.preventDefault();
                                                    }
                                                }}
                                            />
                                        </Form.Item>
                                    )}

                                    {/* Show amount in words for Quoted stage */}
                                    {selectedStage && selectedStage === "Quoted" && quoteAmount > 0 && (
                                        <div style={{
                                            marginTop: '-8px',
                                            marginBottom: '16px',
                                            padding: '8px 12px',
                                            backgroundColor: '#f6ffed',
                                            border: '1px solid #b7eb8f',
                                            borderRadius: '4px',
                                            fontSize: '12px',
                                            color: '#389e0d',
                                            fontStyle: 'italic'
                                        }}>
                                            <strong>Amount in words:</strong> {numberToWords(quoteAmount)} Only
                                        </div>
                                    )}

                                  {/* ============ PAYMENT SECTION (Handles both Payment Details & Pending Payment) ============ */}
{/* ============ PAYMENT SECTION (Handles both Payment Details & Pending Payment) ============ */}
{(selectedStage === "Payment Details" || selectedStage === "Pending Payment") && (
    <>
       {/* Full Payment Warning Alert */}
        {fullPaymentAlert && selectedStage === "Pending Payment" && (
            <Alert
                message="⚠️ Full Payment Not Allowed in Pending Payment"
                description="Please go to 'Payment Details' stage to record the full payment amount."
                type="error"
                showIcon
                closable
                onClose={() => setFullPaymentAlert(false)}
                style={{ marginBottom: 16 }}
            />
        )}
        <Divider orientation="left">
            <DollarOutlined /> 
            {selectedStage === "Pending Payment" ? "Pending Payment" : "Payment Details"}
        </Divider>

        {/* Show different labels based on stage */}
        {selectedStage === "Pending Payment" && (
            <Alert
                message="Pending Payment"
                description="Record an advance payment for this lead."
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
            />
        )}

        {/* Payment Amount - Different behavior for Pending Payment */}
        <Form.Item
            name="paymentAmount"
            label={(() => {
                const { totalAmount, totalPaid, balance } = getPaymentAmountBreakdown();
                return (
                    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', columnGap: 12, rowGap: 2, width: '100%' }}>
                        <Space size={6}>
                            <DollarOutlined style={{ color: '#52c41a' }} />
                            <span>Payment Amount</span>
                        </Space>
                        <span style={{ display: 'flex', flexWrap: 'wrap', columnGap: 10 }}>
                            <Text type="secondary" style={{ fontSize: 12, fontWeight: 400 }}>
                                Total: ${totalAmount.toFixed(2)}
                            </Text>
                            <Text type="secondary" style={{ fontSize: 12, fontWeight: 400 }}>
                                Paid: ${totalPaid.toFixed(2)}
                            </Text>
                            <Text type="secondary" style={{ fontSize: 12, fontWeight: 400 }}>
                                Balance: ${balance.toFixed(2)}
                            </Text>
                        </span>
                    </div>
                );
            })()}
        rules={[
    { required: true, message: 'Please enter payment amount' },
    { type: 'number', min: 0.01, message: 'Amount must be greater than 0' },
    ({ getFieldValue }) => ({
        validator(_, value) {
            // Calculate balance for validation
            const { balance: currentBalance } = getPaymentAmountBreakdown();

            // For Payment Details: Strict validation
            if (selectedStage === "Payment Details") {
                if (value && value > currentBalance) {
                    return Promise.reject(new Error(`Payment amount cannot exceed balance of $${currentBalance.toFixed(2)}`));
                }
            }
            
            // For Pending Payment: Warning only (no rejection)
            // Just show a warning message but allow submission
            if (selectedStage === "Pending Payment") {
                if (value && value > currentBalance && currentBalance > 0) {
                    // Show warning but don't block submission
                                        return Promise.reject(new Error(`Payment amount cannot exceed balance of $${currentBalance.toFixed(2)}`));

                }
            }
            
            return Promise.resolve();
        }
    })
]}
            // Only set initial value for Payment Details, not for Pending Payment
            initialValue={selectedStage === "Payment Details" ? (() => {
                const { balance } = getPaymentAmountBreakdown();
                return balance > 0 ? balance : undefined;
            })() : undefined}
        >
            <InputNumber 
                style={{ width: '100%' }}
                placeholder={selectedStage === "Pending Payment" ? "Enter advance payment amount" : "Enter payment amount"}
                prefix="$"
                min={0.01}
                step={0.01}
                precision={2}
                formatter={(value) => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
                size="large"
            />
        </Form.Item>

        {/* Payment Method */}
        <Form.Item
            name="paymentMethod"
            label="Payment Method"
            rules={[{ required: true, message: 'Please select payment method' }]}
        >
            <Select
                placeholder="Select payment method"
                onChange={(value) => setPaymentMethod(value)}
            >
                {paymentMethods.map(method => (
                    <Option key={method} value={method}>
                        <Space>
                            {getPaymentMethodIcon(method)}
                            {method}
                        </Space>
                    </Option>
                ))}
            </Select>
        </Form.Item>

        {/* Payment Method Specific Fields */}
        {renderPaymentMethodFields()}

        {/* Billing and Shipping Address Section */}
        {(() => {
            const noBillingMethods = [
                'Cashapp',
                'Venmo',
                'PayPal',
                'Pay Tomorrow',
                'ACH',
                'Zelle',
                'Cash',
                'Check Payment',
                'Wire Transfer',
        
            ];
            const requiresBillingAddress = !noBillingMethods.includes(paymentMethod);
            const isAddressRequiredPaymentStage = selectedStage === "Payment Details" || selectedStage === "Pending Payment";
            const isBusinessNameRequired = isAddressRequiredPaymentStage && requiresBillingAddress && !sameAsBillingValue;
            
            return (
                <>
                    {/* Billing Address */}
                    {requiresBillingAddress && (
                        <Card 
                            size="small" 
                            title={
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <HomeOutlined />
                                    <span>Billing Address</span>
                                    {isAddressRequiredPaymentStage && (
                                        <Tag color="red" size="small">Required</Tag>
                                    )}
                                </div>
                            } 
                            style={{ marginBottom: 16 }}
                        >
                            <Form.Item
                                name="billingStreet"
                                label="Street Address"
                                rules={[
                                    { 
                                        required: isAddressRequiredPaymentStage && requiresBillingAddress,
                                        message: 'Street address is required for payment' 
                                    }
                                ]}
                            >
                                <Input placeholder="123 Main St" />
                            </Form.Item>

                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item
                                        name="billingCity"
                                        label="City"
                                        rules={[
                                            { 
                                                required: isAddressRequiredPaymentStage && requiresBillingAddress,
                                                message: 'City is required for payment' 
                                            }
                                        ]}
                                    >
                                        <Input placeholder="City" />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item
                                        name="billingState"
                                        label="State"
                                        rules={[
                                            { 
                                                required: isAddressRequiredPaymentStage && requiresBillingAddress,
                                                message: 'State is required for payment' 
                                            }
                                        ]}
                                    >
                                        <Input placeholder="State" />
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Form.Item
                                name="billingZip"
                                label="ZIP Code"
                                rules={[
                                    { 
                                        required: isAddressRequiredPaymentStage && requiresBillingAddress,
                                        message: 'ZIP code is required for payment' 
                                    },
                                    { 
                                        pattern: /^\d{5}(-\d{4})?$/, 
                                        message: 'Valid ZIP code required (5 or 9 digits)' 
                                    }
                                ]}
                            >
                                <Input placeholder="12345" maxLength={10} />
                            </Form.Item>

                            {/* Same as Billing Checkbox - For both Payment Details and Pending Payment */}
                            <Form.Item name="sameAsBilling" valuePropName="checked">
                                <Checkbox
                                    onChange={(e) => {
                                        const checked = e.target.checked;
                                        if (checked) {
                                            const billingValues = form.getFieldsValue([
                                                'billingStreet', 'billingCity', 'billingState', 'billingZip'
                                            ]);
                                            form.setFieldsValue({
                                                shippingStreet: billingValues.billingStreet,
                                                shippingCity: billingValues.billingCity,
                                                shippingState: billingValues.billingState,
                                                shippingZip: billingValues.billingZip
                                            });
                                        } else {
                                            form.setFieldsValue({
                                                shippingStreet: '',
                                                shippingCity: '',
                                                shippingState: '',
                                                shippingZip: ''
                                            });
                                        }
                                    }}
                                >
                                    Shipping address same as billing
                                </Checkbox>
                            </Form.Item>
                        </Card>
                    )}

                    {/* Business Name - Always show */}
                    <Row gutter={16}>
                        <Col span={24}>
                            <Form.Item
                                name="businessName"
                                label="Business Name"
                                dependencies={['sameAsBilling']}
                                required={isBusinessNameRequired}
                                rules={[
                                    ({ getFieldValue }) => ({
                                        validator(_, value) {
                                            const required = isAddressRequiredPaymentStage && requiresBillingAddress && !getFieldValue('sameAsBilling');
                                            if (required && (!value || !value.toString().trim())) {
                                                return Promise.reject(new Error('Business name is required when shipping address is different from billing'));
                                            }
                                            return Promise.resolve();
                                        },
                                    }),
                                ]}
                            >
                                <Input placeholder="Enter business name" />
                            </Form.Item>
                        </Col>
                    </Row>

                    {/* Customer Name Field (read-only/prefilled) - Always show */}
                    <Row gutter={16}>
                        <Col span={24}>
                            <Form.Item
                                name="customerName"
                                label="Customer Name"
                            >
                                <Input
                                    placeholder={lead?.personalInfo?.fullName}
                                    value={lead?.personalInfo?.fullName || ''}
                                    readOnly
                                    disabled
                                    style={{ backgroundColor: '#f5f5f5' }}
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    {/* ATTN Name - only when shipping is different from billing (defaults to Customer Name) */}
                    {isAddressRequiredPaymentStage && !(requiresBillingAddress && sameAsBillingValue) && (
                        <Row gutter={16}>
                            <Col span={24}>
                                <Form.Item
                                    name="attnName"
                                    label="ATTN Name"
                                    tooltip="Person who receives the part at the shipping address. Defaults to Customer Name."
                                >
                                    <Input placeholder={lead?.personalInfo?.fullName || 'Enter ATTN name'} />
                                </Form.Item>
                            </Col>
                        </Row>
                    )}

                    {/* Shipping Address Section - Show for both Payment Details and Pending Payment */}
                    {(selectedStage === "Payment Details" || selectedStage === "Pending Payment") && (
                        <Card 
                            size="small" 
                            title={
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <HomeOutlined />
                                    <span>Shipping Address</span>
                                    {isAddressRequiredPaymentStage && (
                                        <Tag color="red" size="small">Required</Tag>
                                    )}
                                </div>
                            } 
                            style={{ marginBottom: 16 }}
                        >
                            <Form.Item
                                name="shippingStreet"
                                label="Shipping Street Address"
                                rules={[
                                    ({ getFieldValue }) => ({
                                        validator(_, value) {
                                            const sameAsBilling = getFieldValue('sameAsBilling');
                                            if (isAddressRequiredPaymentStage) {
                                                if (!sameAsBilling && !value) {
                                                    return Promise.reject(new Error('Shipping street address is required'));
                                                }
                                            }
                                            return Promise.resolve();
                                        },
                                    }),
                                ]}
                            >
                                <Input placeholder="456 Delivery St" />
                            </Form.Item>

                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item
                                        name="shippingCity"
                                        label="Shipping City"
                                        rules={[
                                            ({ getFieldValue }) => ({
                                                validator(_, value) {
                                                    const sameAsBilling = getFieldValue('sameAsBilling');
                                                    if (isAddressRequiredPaymentStage) {
                                                        if (!sameAsBilling && !value) {
                                                            return Promise.reject(new Error('Shipping city is required'));
                                                        }
                                                    }
                                                    return Promise.resolve();
                                                },
                                            }),
                                        ]}
                                    >
                                        <Input placeholder="City" />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item
                                        name="shippingState"
                                        label="Shipping State"
                                        rules={[
                                            ({ getFieldValue }) => ({
                                                validator(_, value) {
                                                    const sameAsBilling = getFieldValue('sameAsBilling');
                                                    if (isAddressRequiredPaymentStage) {
                                                        if (!sameAsBilling && !value) {
                                                            return Promise.reject(new Error('Shipping state is required'));
                                                        }
                                                    }
                                                    return Promise.resolve();
                                                },
                                            }),
                                        ]}
                                    >
                                        <Input placeholder="State" />
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Form.Item
                                name="shippingZip"
                                label="Shipping ZIP Code"
                                rules={[
                                    ({ getFieldValue }) => ({
                                        validator(_, value) {
                                            const sameAsBilling = getFieldValue('sameAsBilling');
                                            if (isAddressRequiredPaymentStage) {
                                                if (!sameAsBilling && !value) {
                                                    return Promise.reject(new Error('Shipping ZIP code is required'));
                                                }
                                                if (value && !/^\d{5}(-\d{4})?$/.test(value)) {
                                                    return Promise.reject(new Error('Valid ZIP code required (5 or 9 digits)'));
                                                }
                                            }
                                            return Promise.resolve();
                                        },
                                    }),
                                ]}
                            >
                                <Input placeholder="54321" maxLength={10} />
                            </Form.Item>
                        </Card>
                    )}
                </>
            );
        })()}
    </>
)}

                                    {/* Show comments field for Negotiation and other stages requiring comments */}
                                    {selectedStage && selectedStage !== currentStage && showCommentsField && (
                                        <Form.Item
                                            name="comments"
                                            label={
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                    <CommentOutlined style={{ color: '#ff4d4f' }} />
                                                    <span>Comments</span>
                                                    {stagesRequiringMandatoryComments.includes(selectedStage) && (
                                                        <Tag color={"red"} style={{ marginLeft: 'auto' }}>
                                                            Required
                                                        </Tag>
                                                    )}
                                                </div>
                                            }
                                            rules={[
                                                {
                                                    required: stagesRequiringMandatoryComments.includes(selectedStage),
                                                    message: `Comments are required for ${selectedStage} stage`
                                                },
                                                { max: 1000, message: 'Comments cannot exceed 1000 characters' }
                                            ]}
                                            tooltip={
                                                selectedStage === "Negotiation"
                                                    ? "Enter negotiation details and next steps"
                                                    : `Enter detailed comments about why you're changing to "${selectedStage}"`
                                            }
                                        >
                                            <TextArea
                                                placeholder={
                                                    selectedStage === "Negotiation"
                                                        ? "Enter negotiation details, agreed terms, and next steps..."
                                                        : stagesWithOptionalComments.includes(selectedStage)
                                                            ? `Optional note for "${selectedStage}" (e.g. left voicemail at 3 PM)...`
                                                            : `Enter detailed comments about why you're changing to "${selectedStage}"...`
                                                }
                                                rows={selectedStage === "Negotiation" ? 6 : 4}
                                                showCount
                                                maxLength={1000}
                                                autoSize={{
                                                    minRows: selectedStage === "Negotiation" ? 6 : 4,
                                                    maxRows: selectedStage === "Negotiation" ? 10 : 8
                                                }}
                                            />
                                        </Form.Item>
                                    )}
{selectedStage && selectedStage === "Follow-Up" && (
    <>
        {/* Follow-Up Date Field */}
        <Form.Item
            name="followUpDate"
            label={
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <FieldTimeOutlined style={{ color: '#1890ff' }} />
                    <span>Follow-Up Date & Time (PST)</span>
                    <Tag color="blue" style={{ marginLeft: 'auto' }}>
                        Required
                    </Tag>
                </div>
            }
            rules={[
                { required: true, message: 'Follow-up date is required for Follow-Up stage' },
                {
                    validator: (_, value) => {
                        if (value) {
                            const selectedDate = dayjs(value).tz('America/Los_Angeles', true);
                            const nowPST = dayjs().tz('America/Los_Angeles');
                            
                            if (selectedDate.isBefore(nowPST)) {
                                return Promise.reject(new Error('Follow-up date/time cannot be in the past'));
                            }
                        }
                        return Promise.resolve();
                    }
                }
            ]}
            tooltip="Select the date in PST when you need to follow up with this lead"
        >
            <DatePicker
                style={{ width: '100%' }}
                onChange={handleFollowUpDateChange}
                disabledDate={(current) => {
                    const todayPST = dayjs().tz('America/Los_Angeles').startOf('day');
                    return current && current.startOf('day') < todayPST;
                }}
                showTime={{ format: 'hh:mm A', use12Hours: true, defaultOpenValue: dayjs().tz('America/Los_Angeles') }}
                format="MMM DD, YYYY hh:mm A"
                placeholder="Select follow-up date and time (PST)"
                getPopupContainer={(trigger) => trigger.parentNode}
            />
        </Form.Item>

        {/* Comments Field for Follow-Up */}
        <Form.Item
            name="comments"
            label={
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 16 }}>
                    <CommentOutlined style={{ color: '#ff4d4f' }} />
                    <span>Follow-Up Comments</span>
                    <Tag color="red" style={{ marginLeft: 'auto' }}>
                        Required
                    </Tag>
                </div>
            }
            rules={[
                { required: true, message: 'Comments are required for Follow-Up stage' },
                { max: 1000, message: 'Comments cannot exceed 1000 characters' }
            ]}
            tooltip="Enter details about why you're following up and what needs to be discussed"
        >
            <TextArea
                placeholder="Enter follow-up details, reason for follow-up, and what needs to be discussed..."
                rows={4}
                showCount
                maxLength={1000}
                autoSize={{
                    minRows: 4,
                    maxRows: 8
                }}
            />
        </Form.Item>
    </>
)}

{/* Sales Made Date Picker - Show only when Sales Made stage is selected */}
{selectedStage === "Sales Made" && showSalesMadeDatePicker && (
    <>
        <Form.Item
            name="orderNumber"
            label={
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <FileTextOutlined style={{ color: '#1890ff' }} />
                    <span>Order Number</span>
                    <Tag color="red" style={{ marginLeft: 'auto' }}>
                        Required
                    </Tag>
                </div>
            }
            rules={[
                { required: true, message: 'Order number is required for Sales Made stage' },
                { min: 1, message: 'Order number cannot be empty' },
                { max: 100, message: 'Order number cannot exceed 100 characters' }
            ]}
            tooltip="Enter order number - this is required for completed sales"
            initialValue={lead?.orderNumber || ''}
        >
            <Input
                placeholder="Enter order number (e.g., PO-12345, INV-001, or any reference)"
                size="large"
                onChange={(e) => setHasMadeChanges(true)}
                allowClear={false}
            />
        </Form.Item>

        <Form.Item
            name="salesMadeDate"
            label={
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <CalendarOutlined style={{ color: '#52c41a' }} />
                    <span>Sales Made Date</span>
                    <Tag color="green" style={{ marginLeft: 'auto' }}>
                        Required
                    </Tag>
                </div>
            }
            rules={[
                { required: true, message: 'Please select the date when sale was made' }
            ]}
            tooltip="Select the date when the sale was completed"
        >
            <DatePicker
                style={{ width: '100%' }}
                onChange={(date) => {
                    setSalesMadeDate(date);
                    setHasMadeChanges(true);
                }}
                format="MMM DD, YYYY"
                placeholder="Select sales made date"
                size="large"
            />
        </Form.Item>

        <Form.Item
            name="milesPromiseCustomer"
            label={
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <CommentOutlined style={{ color: '#52c41a' }} />
                    <span>Miles Promise Customer</span>
                    <Tag color="red" style={{ marginLeft: 'auto' }}>
                        Required
                    </Tag>
                </div>
            }
            rules={[
                { required: true, message: 'Miles Promise Customer comment is required for Sales Made stage' },
                { min: 5, message: 'Please provide at least 5 characters' },
                { max: 1000, message: 'Comment cannot exceed 1000 characters' }
            ]}
            tooltip="Document the customer's miles promise agreement"
        >
            <TextArea
                placeholder="Enter details about the miles promise agreement with customer..."
                rows={4}
                showCount
                maxLength={1000}
                autoSize={{
                    minRows: 2,
                    maxRows: 4
                }}
                onChange={(e) => setHasMadeChanges(true)}
            />
        </Form.Item>

        <Form.Item
            name="trackingPromiseDays"
            label={
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <CalendarOutlined style={{ color: '#1890ff' }} />
                    <span># of Days Promised for Tracking</span>
                    <Tag color="red" style={{ marginLeft: 'auto' }}>
                        Required
                    </Tag>
                </div>
            }
            rules={[
                { required: true, message: '# of Days Promised for Tracking is required' },
                {
                    validator: (_, value) => {
                        if (isValidTrackingPromiseDays(value)) {
                            return Promise.resolve();
                        }

                        return Promise.reject(new Error(TRACKING_PROMISE_DAYS_ERROR));
                    }
                }
            ]}
            tooltip="Enter a single value or range promised to provide tracking"
            initialValue={lead?.trackingPromiseDays}
        >
            <Input
                placeholder={TRACKING_PROMISE_DAYS_PLACEHOLDER}
                size="large"
                style={{ width: '100%' }}
                onChange={() => setHasMadeChanges(true)}
            />
        </Form.Item>

        {/* ✅ ADD TYPE OF LEAD FIELD HERE - After trackingPromiseDays */}
        <Form.Item
            name="typeOfLead"
            label={
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Tag icon={<EyeOutlined />} color="purple" style={{ margin: 0 }}>
                        Type Of Lead
                    </Tag>
                    <Tag color="red" style={{ marginLeft: 'auto' }}>
                        Required
                    </Tag>
                </div>
            }
            rules={[
                { required: true, message: 'Please select Type Of Lead' },
                {
                    validator: (_, value) => (
                        TYPE_OF_LEAD_OPTIONS.includes(value)
                            ? Promise.resolve()
                            : Promise.reject(new Error('Please select Type Of Lead'))
                    )
                }
            ]}
            tooltip="Select how this lead was generated or classified"
            initialValue={getTypeOfLeadFormValue(lead?.typeOfLead)}
        >
            <Select
                placeholder="Select Type Of Lead"
                size="large"
                onChange={() => setHasMadeChanges(true)}
                allowClear
            >
                <Option value="Inbound Call">
                    <Space>
                        <PhoneOutlined style={{ color: '#1890ff' }} />
                        Inbound Call
                    </Space>
                </Option>
                <Option value="Live">
                    <Space>
                        <CheckCircleOutlined style={{ color: '#52c41a' }} />
                        Live
                    </Space>
                </Option>
                <Option value="Follow-Up">
                    <Space>
                        <CalendarOutlined style={{ color: '#faad14' }} />
                        Follow-Up
                    </Space>
                </Option>
            </Select>
        </Form.Item>
    </>
)}


                                </>
                            )}
                        </Form>
                    </Card>
                </Col>
            </Row>
        </Drawer>
    );
};
// Helper function for stage colors
const stageColorMap = {
    "New": "blue",
    "Call Back": "orange",
    "Voice Mail": "purple",
    "Follow-Up": "cyan",
    "Quoted": "green",
    "Pending Payment": "orange",
    "Negotiation": "gold",
    "Payment Details": "lime",
    "Payment Completed": "success",
    "Sales Made": "success",
    "Part Not Available": "warning",
    "Wrong Number": "red",
    "Not Interested": "red",
    "Hang Up": "red",
    "Fake": "red",
    "Test Lead": "default",
    "Error": "error"
};

export default EditLeadDrawer;

